import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form, FormGroup, FormField } from "semantic-ui-react";

// Component
import ModPaymentDetail from "./ModPaymentDetail";
import ModReceivePayment from "./ModReceivePayment";
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import DateTextBox from "react-lib/apps/common/DateTextBox";
import { Table } from "react-lib/frameworks/Table";

// Serializers
import EncounterBillingSummarySerializer from "issara-sdk/types/EncounterBillingSummarySerializer_apps_BIL";

// Types
import {
  ACTIONS,
  BTN_ACTS,
  OnEvent,
  PaymentSummaryType,
  PickedState,
  RunSequence,
  SetProp,
} from "./sequence/PaymentSummary";

// Utils
import { formatComma } from "react-lib/utils/tsUtils";
import { formatISOtoBEDateTime } from "react-lib/utils/dateUtils";
import { SelectedCustomerData } from "./types/MeBillingType";

type CardPaymentSummaryProps = {
  setProp: SetProp;
  runSequence: RunSequence;
  onEvent: OnEvent;
  // data
  PaymentSummarySequence?: PaymentSummaryType;
  selectedCustomer?: SelectedCustomerData;
  //
  languageUX: "en" | "th";
} & Pick<PickedState, "buttonLoadCheck">;

export type PaymentSummaryFilter = {
  start_date: string;
  end_date: string;
};

const CardPaymentSummary = (props: CardPaymentSummaryProps) => {
  const [openDetailMod, setOpenDetailMod] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  useEffect(() => {
    props.runSequence({
      sequence: "PaymentSummary",
      restart: true,
      action: ACTIONS.SEARCH,
    });
  }, []);

  const paymentSummaryList = useMemo(
    () =>
      (props.PaymentSummarySequence?.billingSummary ?? []).map(
        (item: EncounterBillingSummarySerializer) => {
          const total = Number(item?.total ?? 0);
          const discount = Number(item?.discount ?? 0);
          const amount = total - discount;

          return {
            ...item,
            number: flexTable()(item?.number),
            created: flexTable()(formatISOtoBEDateTime(item?.created)?.dateTimeShortBracket),
            provider_name: flexTable("flex-start")(item?.provider_name),
            total: flexTable("flex-end")(formatComma(String(total))),
            discount: flexTable("flex-end")(formatComma(String(discount))),
            amount: flexTable("flex-end")(formatComma(String(amount))),
            paid: flexTable("flex-end")(formatComma(String(item?.paid ?? 0))),
            unpaid: flexTable("flex-end")(formatComma(String(item?.unpaid ?? 0))),
          };
        }
      ),
    [props.PaymentSummarySequence?.billingSummary]
  );

  const handleSearch = () => {
    props.runSequence({
      sequence: "PaymentSummary",
      action: ACTIONS.SEARCH,
    });

    setSelectedRow(null);
  };

  const handleCloseMod = () => {
    setOpenDetailMod(false);
  };

  const handleChangeDate = (type: "startDate" | "endDate", date: string) => {
    props.setProp(`PaymentSummarySequence.billingSummaryFilter.${type}`, date);
  };

  const handleRowProps = (state: any, rowInfo: any, column: any, instance: any) => {
    return {
      style: {
        backgroundColor:
          rowInfo?.original?.id && rowInfo?.original?.id === selectedRow?.id ? "#cccccc" : "white",
      },
      onClick: () => {
        setSelectedRow(rowInfo.original);
      },
    };
  };

  const handleGetPaymentDetail = () => {
    props.runSequence({
      sequence: "PaymentSummary",
      action: ACTIONS.GET_PAYMENT_DETAIL,
      id: selectedRow?.id,
      callback: (open: boolean) => {
        setOpenDetailMod(open);
      },
    });
  };

  const handlePrintPaymentDetail = () => {
    props.runSequence({
      sequence: "PaymentSummary",
      action: ACTIONS.PRINT_PAYMENT_DETAIL,
      id: selectedRow?.id,
    });
  };

  return (
    <div style={{ padding: "1rem" }}>
      {/* --- Mod Detail --- */}
      <ModPaymentDetail
        open={openDetailMod}
        onClose={handleCloseMod}
        paymentDetail={props.PaymentSummarySequence?.billingDetail}
        selectedCustomer={props.selectedCustomer}
      />

      {/* --- Mod Payment Process --- */}
      {/* <ModReceivePayment
        open={openDetailMod}
        onClose={handleCloseMod}
      /> */}

      {/* --- Filter --- */}
      <Form>
        <FormGroup inline>
          <FormField>
            <label>วันที่รับบริการ</label>
          </FormField>
          <FormField>
            <DateTextBox
              value={props.PaymentSummarySequence?.billingSummaryFilter?.startDate}
              onChange={(date: string) => handleChangeDate("startDate", date)}
            />
          </FormField>
          <FormField>
            <label>-</label>
          </FormField>
          <FormField>
            <DateTextBox
              value={props.PaymentSummarySequence?.billingSummaryFilter?.endDate}
              onChange={(date: string) => handleChangeDate("endDate", date)}
            />
          </FormField>
          <FormField>
            <div style={{ width: "100%" }}>
              <ButtonLoadCheck
                // function
                setProp={props.setProp}
                onClick={handleSearch}
                // data
                paramKey={BTN_ACTS.SEARCH}
                buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SEARCH]}
                // config
                style={{ background: "var(--primary-theme-color)", color: "white" }}
                fluid={true}
                size="small"
                title={"ค้นหา"}
              />
            </div>
          </FormField>
        </FormGroup>
      </Form>

      <Table
        className="--rt-tbody-overflow-y-hidden"
        data={paymentSummaryList}
        getTrProps={handleRowProps}
        headers="หมายเลขรับบริการ, วันที่เวลารับบริการ, ผู้ให้บริการ, ค่าบริการรวม, ส่วนลดรวม, ยอดชำระ, ชำระแล้ว, ค้างชำระ"
        keys="number, created, provider_name, total, discount, amount, paid, unpaid"
        showPagination={false}
        style={{ height: "50vh" }}
        widths="150,150,200,auto,auto,auto,auto,auto"
      />

      <Form style={{ marginTop: "1rem" }}>
        <FormGroup inline style={{ justifyContent: "flex-end" }}>
          <FormField>
            <div style={{ width: "100%" }}>
              <ButtonLoadCheck
                // function
                setProp={props.setProp}
                onClick={handleGetPaymentDetail}
                disabled={!selectedRow}
                // data
                paramKey={BTN_ACTS.GET_PAYMENT_DETAIL}
                buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.GET_PAYMENT_DETAIL]}
                // config
                title={"แสดงรายละเอียดค่าใช้จ่าย"}
              />
            </div>
          </FormField>
          <FormField style={{ paddingRight: 0 }}>
            <div style={{ width: "100%" }}>
              <ButtonLoadCheck
                // function
                setProp={props.setProp}
                onClick={handlePrintPaymentDetail}
                disabled={!selectedRow}
                // data
                paramKey={BTN_ACTS.PRINT_PAYMENT_DETAIL}
                buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.PRINT_PAYMENT_DETAIL]}
                // config
                title={"พิมพ์รายการค่าใช้จ่าย"}
              />
            </div>
          </FormField>
        </FormGroup>
      </Form>
    </div>
  );
};

CardPaymentSummary.displayName = "CardPaymentSummary";

export default memo(CardPaymentSummary);

export const flexTable =
  (justify: React.CSSProperties["justifyContent"] = "center") =>
  (children: React.ReactNode) =>
    <div style={{ display: "flex", justifyContent: justify }}>{children}</div>;
